import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Textarea } from './ui/textarea';
import { User } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MentionTextarea = React.forwardRef(({ 
  value, 
  onChange, 
  placeholder = "Write your message...",
  communityType = "free",
  className = "",
  rows = 3
}, forwardedRef) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [mentionQuery, setMentionQuery] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [loading, setLoading] = useState(false);
  const { getAuthHeader } = useAuth();
  const internalRef = useRef(null);
  const textareaRef = forwardedRef || internalRef;
  const suggestionsRef = useRef(null);

  useEffect(() => {
    fetchMembers();
  }, [communityType]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/community/members?community_type=${communityType}`,
        { headers: getAuthHeader() }
      );
      setAllMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch members:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    setCursorPosition(cursorPos);
    
    // Check if we're in a mention context (typing after @)
    const textBeforeCursor = newValue.slice(0, cursorPos);
    const mentionMatch = textBeforeCursor.match(/@([A-Za-z0-9\s]*)$/);
    
    if (mentionMatch) {
      const query = mentionMatch[1].toLowerCase();
      setMentionQuery(query);
      
      // Filter members based on query
      const filtered = allMembers.filter(member => 
        member.name.toLowerCase().includes(query)
      ).slice(0, 5);
      
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setShowSuggestions(false);
    }
    
    onChange(newValue);
  };

  const insertMention = (member) => {
    const textBeforeCursor = value.slice(0, cursorPosition);
    const textAfterCursor = value.slice(cursorPosition);
    
    // Find the @ position
    const mentionMatch = textBeforeCursor.match(/@([A-Za-z0-9\s]*)$/);
    if (mentionMatch) {
      const atPosition = textBeforeCursor.lastIndexOf('@');
      const newValue = value.slice(0, atPosition) + `@${member.name} ` + textAfterCursor;
      onChange(newValue);
      
      // Move cursor after the mention
      setTimeout(() => {
        if (textareaRef.current) {
          const newCursorPos = atPosition + member.name.length + 2;
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    }
    
    setShowSuggestions(false);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;
    
    if (e.key === 'Escape') {
      setShowSuggestions(false);
    } else if (e.key === 'Tab' || e.key === 'Enter') {
      if (suggestions.length > 0) {
        e.preventDefault();
        insertMention(suggestions[0]);
      }
    }
  };

  return (
    <div className="relative">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
        rows={rows}
      />
      
      {showSuggestions && suggestions.length > 0 && (
        <div 
          ref={suggestionsRef}
          className="absolute left-0 right-0 bottom-full mb-1 bg-white rounded-lg shadow-xl border border-stone-200 overflow-hidden z-50"
        >
          <div className="p-2 text-xs text-stone-500 bg-stone-50 border-b">
            Type to mention someone
          </div>
          {suggestions.map((member) => (
            <button
              key={member.id}
              type="button"
              onClick={() => insertMention(member)}
              className="w-full flex items-center gap-3 p-3 hover:bg-stone-50 transition-colors text-left"
            >
              {member.avatar_url ? (
                <img 
                  src={member.avatar_url} 
                  alt={member.name}
                  className="w-8 h-8 rounded-full object-cover"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </div>
              )}
              <span className="font-medium text-stone-900">{member.name}</span>
            </button>
          ))}
        </div>
      )}
      
      <p className="mt-1 text-xs text-stone-400">
        Tip: Type @ to mention someone
      </p>
    </div>
  );
});
MentionTextarea.displayName = 'MentionTextarea';

export default MentionTextarea;
